import React, { useCallback, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  PhotoIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from './Button';
import { Body, Caption } from './Typography';
import { Badge } from './Badge';

export interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in bytes
  maxFiles?: number;
  disabled?: boolean;
  dragDropText?: string;
  browseText?: string;
  className?: string;
}

export interface UploadedFile {
  file: File;
  id: string;
  status: 'uploading' | 'success' | 'error';
  progress?: number;
  error?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  accept = '*/*',
  multiple = false,
  maxSize = 10 * 1024 * 1024, // 10MB
  maxFiles = 5,
  disabled = false,
  dragDropText = 'Drag and drop files here, or click to browse',
  browseText = 'Browse files',
  className = '',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.size > maxSize) {
      return `File size exceeds ${formatFileSize(maxSize)}`;
    }
    return null;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return PhotoIcon;
    }
    return DocumentIcon;
  };

  const handleFiles = useCallback((fileList: FileList) => {
    setError(null);
    const newFiles: File[] = [];
    
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const validationError = validateFile(file);
      
      if (validationError) {
        setError(validationError);
        return;
      }
      
      if (files.length + newFiles.length >= maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        break;
      }
      
      newFiles.push(file);
    }

    if (newFiles.length > 0) {
      const uploadedFiles: UploadedFile[] = newFiles.map(file => ({
        file,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        status: 'success',
      }));
      
      setFiles(prev => [...prev, ...uploadedFiles]);
      onFilesChange([...files.map(f => f.file), ...newFiles]);
    }
  }, [files, maxFiles, maxSize, onFilesChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  }, [disabled, handleFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
  }, [handleFiles]);

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const updated = prev.filter(f => f.id !== id);
      onFilesChange(updated.map(f => f.file));
      return updated;
    });
  };

  const clearAllFiles = () => {
    setFiles([]);
    onFilesChange([]);
    setError(null);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          isDragOver
            ? 'border-primary-500 bg-primary-50'
            : error
            ? 'border-red-300 bg-red-50'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileInput}
          disabled={disabled}
          className="hidden"
        />
        
        <motion.div
          animate={{ scale: isDragOver ? 1.05 : 1 }}
          transition={{ duration: 0.2 }}
          className="flex flex-col items-center space-y-4"
        >
          <CloudArrowUpIcon className={`w-12 h-12 ${isDragOver ? 'text-primary-500' : 'text-gray-400'}`} />
          <div>
            <Body className="text-gray-900 mb-1">
              {dragDropText}
            </Body>
            <Caption className="text-gray-500">
              {accept !== '*/*' && `Supported formats: ${accept}`}
              {maxSize && ` • Max size: ${formatFileSize(maxSize)}`}
              {multiple && ` • Max files: ${maxFiles}`}
            </Caption>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleBrowseClick}
            disabled={disabled}
          >
            {browseText}
          </Button>
        </motion.div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <Caption className="text-red-600">{error}</Caption>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Caption className="text-gray-700 font-medium">
              {files.length} file{files.length !== 1 ? 's' : ''} selected
            </Caption>
            <Button
              variant="secondary"
              size="sm"
              onClick={clearAllFiles}
              disabled={disabled}
            >
              Clear all
            </Button>
          </div>
          
          <div className="space-y-2">
            {files.map((uploadedFile) => {
              const Icon = getFileIcon(uploadedFile.file);
              
              return (
                <div
                  key={uploadedFile.id}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <Caption className="font-medium text-gray-900 truncate">
                        {uploadedFile.file.name}
                      </Caption>
                      <Caption className="text-gray-500">
                        {formatFileSize(uploadedFile.file.size)}
                      </Caption>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {uploadedFile.status === 'success' && (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    )}
                    {uploadedFile.status === 'error' && (
                      <Badge variant="danger" size="sm">
                        Error
                      </Badge>
                    )}
                    <button
                      onClick={() => removeFile(uploadedFile.id)}
                      disabled={disabled}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export { FileUpload };
export default FileUpload;
